import { useContext, useEffect, useRef, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { useParams } from "react-router-dom";
import { Form, Button, Input, Tag } from "rsuite";
import {
  addPromotionAction,
  addPromotionRule,
  deletePromotionAction,
  deletePromotionRule,
  getPromotion,
  updatePromotion,
} from "../services/sales/PromoApi";
import { Promotion } from "../models/sales/promotion";
import { MdOutlineImageNotSupported } from "react-icons/md";
import {
  Carousel,
  DateRangePicker,
  IconButton,
  Modal,
  SelectPicker,
  Toggle,
} from "rsuite";
import { TrashIcon } from "@heroicons/react/24/outline";
import { errorToast, successToast } from "../utils/helper-ui";
import { LoadingContext } from "../contexts/LoadingContext";
import { BsTrash3 } from "react-icons/bs";
import ImageIcon from "@rsuite/icons/Image";
import SaveIcon from "@rsuite/icons/Save";
import { uploadFile } from "../services/FileApi";
import { Editor } from "@tinymce/tinymce-react";
import moment from "moment";
import PlusIcon from "@rsuite/icons/Plus";
import { Product } from "../models/inventory/product";
import { getProducts } from "../services/inventory/ProductApi";
import { ProductCategory } from "../models/inventory/product_category";
import { getProductCategories } from "../services/inventory/ProductCategoryApi";
interface PromotionDetailProps {}

const PromotionDetail: FC<PromotionDetailProps> = ({}) => {
  const { promotionId } = useParams();
  const [mounted, setMounted] = useState(false);
  const [promotion, setPromotion] = useState<Promotion>();
  const fileRef = useRef<HTMLInputElement>(null);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [editorContent, setEditorContent] = useState("");
  const [modalAddRule, setModalAddRule] = useState(false);
  const [modalAddRuleType, setModalAddRuleType] = useState("PRODUCTS");
  const [modalAddActionType, setModalAddActionType] = useState("DISCOUNT");
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    ProductCategory[]
  >([]);
  const [modalAddActionValue, setModalAddActionValue] = useState("");
  const [modalAddAction, setModalAddAction] = useState(false);
  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;

    getDetail();
  }, [mounted]);

  const getDetail = async () => {
    try {
      setIsLoading(true);
      let resp = await getPromotion(promotionId!);
      let respJson = await resp.json();
      setPromotion(respJson.data);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeImage = async (id: string) => {
    try {
      const confirmRemoval = window.confirm(
        "Are you sure you want to remove this image?"
      );
      if (!confirmRemoval) return;
      setIsLoading(true);
      // await deleteProductImage(productId!, id);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Promotion Detail</h1>
      <div>{/* Add any additional buttons or elements here if needed */}</div>
    </div>
  );

  const renderImages = () => (
    <div className="w-full">
      <Carousel
        className="custom-slider"
        shape="bar"
        placement="right"
        color="orange"
      >
        {(promotion?.images ?? []).map((img, index) => (
          <div className="relative" key={index}>
            <img
              key={index}
              className="object-cover"
              src={img.url}
              height="250"
            />
            <div className="absolute top-0 right-0 cursor-pointer p-2 aspect-square rounded-full bg-black bg-opacity-20">
              <BsTrash3
                className=""
                onClick={() => removeImage(img.id)}
                size={16}
                color="white"
              />
            </div>
          </div>
        ))}
      </Carousel>
      <IconButton
        appearance="primary"
        color="orange"
        size="sm"
        className="mt-4"
        onClick={() => {
          fileRef.current?.click();
        }}
        icon={<ImageIcon />}
      >
        Add Image
      </IconButton>
    </div>
  );
  const renderInputFile = () => (
    <input
      type="file"
      accept="image/*"
      ref={fileRef}
      className="hidden"
      onChange={async (val) => {
        if (val.target.files?.length == 0) return;
        try {
          setIsLoading(true);
          let resp = await uploadFile(
            val.target.files![0],
            {
              ref_id: promotionId,
              ref_type: "promotion",
            },
            (progress) => {
              console.log(progress);
            }
          );
          getDetail();
        } catch (error) {
          errorToast(`${error}`);
        } finally {
          setIsLoading(false);
        }
      }}
    />
  );
  const handleEditorChange = (content: any, editor: any) => {
    setEditorContent(content);
  };

  const saveNewRule = async () => {
    try {
      setIsLoading(true);
      var newRule = {
        rule_type: modalAddRuleType,
        rule_value: "",
      };
      switch (modalAddRuleType) {
        case "PRODUCTS":
          newRule.rule_value = selectedProducts.map((p) => p.id).join(",");
          break;
        case "CATEGORIES":
          newRule.rule_value = selectedCategories.map((p) => p.id).join(",");
          break;
      }
      await addPromotionRule(promotionId!, newRule);
      successToast("Promotion Rule saved");
      getDetail();
      setModalAddRule(false);
      setSelectedProducts([]);
      setSelectedCategories([]);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveNewAction = async () => {
    try {
      setIsLoading(true);
      var newAction = {
        action_type: modalAddActionType,
        action_value: "",
      };
      switch (modalAddActionType) {
      }
      await addPromotionAction(promotionId!, newAction);
      successToast("Promotion Action saved");
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <MainLayout header={renderHeader()}>
      <div className="container">
        <div className="grid grid-cols-12 gap-4  h-full">
          <div className="col-span-4">
            <div className="w-full min-h-[300px] flex flex-col items-center justify-between">
              {(promotion?.images ?? []).length == 0 ? (
                <div className="bg-gray-50 w-full flex flex-col items-center justify-center h-300 py-10 hover:bg-gray-100">
                  <MdOutlineImageNotSupported
                    size={100}
                    color="gray"
                    className="cursor-pointer"
                    onClick={() => {
                      fileRef.current?.click();
                    }}
                  />
                </div>
              ) : (
                renderImages()
              )}
            </div>
          </div>
          <div className="col-span-8 space-y-4">
            <div className="flex flex-row justify-between items-start ">
              <div>
                <h1 className="text-2xl font-bold">{promotion?.name}</h1>
              </div>
              <div>
                <IconButton
                  appearance="primary"
                  color="green"
                  size="xs"
                  icon={<SaveIcon />}
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      updatePromotion(promotionId!, {
                        ...promotion,
                        description: editorContent,
                      });
                      getDetail();
                      successToast("Promotion saved");
                    } catch (error) {
                      errorToast(`${error}`);
                    } finally {
                      setIsLoading(false);
                    }

                    // Add your edit functionality here
                  }}
                >
                  Save
                </IconButton>
              </div>
            </div>
            <Editor
              apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
              // onInit={(_evt, editor) => (editorRef!.current = editor)}
              initialValue={promotion?.description ?? ""}
              init={{
                height: 500,
                menubar: false,
                plugins: [
                  "advlist",
                  "autolink",
                  "lists",
                  "link",
                  "image",
                  "charmap",
                  "preview",
                  "anchor",
                  "searchreplace",
                  "visualblocks",
                  "code",
                  "fullscreen",
                  "insertdatetime",
                  "media",
                  "table",
                  "code",
                  "help",
                  "wordcount",
                ],
                toolbar:
                  "undo redo | blocks | " +
                  "bold italic forecolor | alignleft aligncenter " +
                  "alignright alignjustify | bullist numlist outdent indent | " +
                  "removeformat | help",
                content_style:
                  "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
              }}
              onEditorChange={handleEditorChange}
            />
            <div className="mb-4">
              <small className="font-bold">Effective:</small>
              <DateRangePicker
                className="w-full"
                format="dd MMM yyyy"
                shouldDisableDate={(date) =>
                  date < moment().subtract(1, "days").toDate()
                }
                value={
                  promotion?.start_date && promotion?.end_date
                    ? [
                        moment(promotion?.start_date).toDate(),
                        moment(promotion?.end_date).toDate(),
                      ]
                    : null
                }
                onChange={(date) => {
                  setPromotion({
                    ...promotion!,
                    start_date: date![0].toISOString(),
                    end_date: date![1].toISOString(),
                  });
                }}
              />
            </div>
            <div className="mb-4 flex flex-row justify-between items-center">
              <small className="font-bold">Active:</small>
              <div>
                <Toggle
                  size={"md"}
                  checked={promotion?.is_active}
                  onChange={(e) => {
                    setPromotion({
                      ...promotion!,
                      is_active: e,
                    });
                  }}
                />
              </div>
            </div>
            <div>
              <div className="mb-4 flex flex-row justify-between items-center">
                <small className="font-bold">Rules:</small>
                <IconButton
                  appearance="ghost"
                  color="green"
                  size="xs"
                  className="ml-2"
                  onClick={() => {
                    setModalAddRule(true);
                  }}
                  icon={<PlusIcon />}
                >
                  Add Rule
                </IconButton>
              </div>
              {(promotion?.rules ?? []).length > 0 ? (
                <table className="w-full border border-gray-300 mt-2">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-xs border text-left border-gray-300">
                        Category
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Value
                      </th>

                      <th className="px-2 py-1 text-xs border border-gray-300">
                        
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotion?.rules?.map((rule, index) => (
                      <tr key={index}>
                        <td className="px-2 py-1 text-xs border border-gray-300">
                          {rule.rule_type}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300">
                          {rule.rule_type == "PRODUCTS" && (
                            <div>
                              {rule.products?.slice(0, 10).map((p) => (
                                <Tag size="sm" key={p.id} color="green">
                                  {p.name}
                                </Tag>
                              ))}
                              {(rule.products ?? []).length > 10 && (
                                <span className="text-xs text-gray-500">
                                  ... {(rule.products ?? []).length - 10} more
                                </span>
                              )}
                            </div>
                          )}
                          {rule.rule_type == "CATEGORIES" && (
                            <div>
                              {rule.categories?.slice(0, 10).map((p) => (
                                <Tag size="sm" key={p.id} color="green">
                                  {p.name}
                                </Tag>
                              ))}
                              {(rule.categories ?? []).length > 10 && (
                                <span className="text-xs text-gray-500">
                                  ... {(rule.categories ?? []).length - 10} more
                                </span>
                              )}
                            </div>
                          )}
                        </td>

                        <td className="px-2 py-1 text-xs border border-gray-300">
                          <div className="flex flex-row justify-center">
                            <TrashIcon
                              className="text-red-500 cursor-pointer w-4"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Are you sure you want to delete promotion rule?`
                                  )
                                ) {
                                  deletePromotionRule(
                                    promotion!.id!,
                                    rule.id!
                                  ).then(() => {
                                    getDetail();
                                  });
                                }
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400">No Rule Available</p>
              )}
            </div>

            <div>
              <div className="mb-4 flex flex-row justify-between items-center">
                <small className="font-bold">Actions:</small>
                <IconButton
                  appearance="ghost"
                  color="green"
                  size="xs"
                  className="ml-2"
                  onClick={() => {
                    setModalAddAction(true);
                  }}
                  icon={<PlusIcon />}
                >
                  Add Action
                </IconButton>
              </div>
              {(promotion?.actions ?? []).length > 0 ? (
                <table className="w-full border border-gray-300 mt-2">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-xs border text-left border-gray-300">
                        Action
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Value
                      </th>

                      <th className="px-2 py-1 text-xs border border-gray-300">
                        
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotion?.actions?.map((action, index) => (
                      <tr key={index}>
                        <td className="px-2 py-1 text-xs border border-gray-300">
                          {action.action_type}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300">
                          {action.action_type == "PRODUCTS" && (
                            <div>
                              
                            </div>
                          )}
                          
                        </td>

                        <td className="px-2 py-1 text-xs border border-gray-300">
                          <div className="flex flex-row justify-center">
                            <TrashIcon
                              className="text-red-500 cursor-pointer w-4"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Are you sure you want to delete promotion action?`
                                  )
                                ) {
                                  deletePromotionAction(
                                    promotion!.id!,
                                    action.id!
                                  ).then(() => {
                                    getDetail();
                                  });
                                }
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400">No Action Available</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {renderInputFile()}
      <Modal
        size={"lg"}
        open={modalAddRule}
        onClose={() => setModalAddRule(false)}
      >
        <Modal.Header>
          <Modal.Title>Add Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="w-full" layout="horizontal" fluid>
            <div className="form-group">
              <label>Type</label>
              <div className="input">
                <SelectPicker
                  block
                  data={[
                    { label: "Min Purchase", value: "MIN_PURCHASE" },
                    { label: "Max Purchase", value: "MAX_PURCHASE" },
                    { label: "Categories", value: "CATEGORIES" },
                    { label: "Products", value: "PRODUCTS" },
                    { label: "Customer Level", value: "CUSTOMER_LEVEL" },
                  ]}
                  searchable={false}
                  onChange={(value) => {
                    setModalAddRuleType(value!);
                  }}
                  value={modalAddRuleType}
                />
              </div>
            </div>
            {modalAddRuleType == "PRODUCTS" && (
              <div className="form-group">
                <label>Products</label>
                <div className="input ">
                  <SelectPicker
                    block
                    data={products}
                    labelKey="name"
                    valueKey="id"
                    searchable={true}
                    onChange={(value) => {
                      let selected = products.find(
                        (v) => v.id == (value as string)
                      );
                      if (selected) {
                        setSelectedProducts((prev) =>
                          prev.map((v) => v.id).includes(value as string)
                            ? prev
                            : [...prev, selected!]
                        );
                      }
                    }}
                    onSearch={async (val) => {
                      if (val == "") return;
                      getProducts({
                        page: 1,
                        size: 10,
                        search: val,
                      })
                        .then((v) => v.json())
                        .then((v) => {
                          setProducts(v.data.items);
                        });
                    }}
                  />
                  <div className="flex flex-row flex-wrap mt-4 gap-y-2">
                    {selectedProducts.map((v) => (
                      <Tag
                        className=""
                        key={v.id}
                        closable
                        onClose={() => {
                          setSelectedProducts((prev) =>
                            prev.filter((vv) => vv.id !== v.id)
                          );
                        }}
                        color={"green"}
                      >
                        {v.name}
                      </Tag>
                    ))}
                  </div>
                  {selectedProducts.length > 0 && (
                    <small>{selectedProducts.length} selected</small>
                  )}
                </div>
              </div>
            )}
            {modalAddRuleType == "CATEGORIES" && (
              <div className="form-group">
                <label>Categories</label>
                <div className="input ">
                  <SelectPicker
                    block
                    data={categories}
                    labelKey="name"
                    valueKey="id"
                    searchable={true}
                    onChange={(value) => {
                      let selected = categories.find(
                        (v) => v.id == (value as string)
                      );
                      if (selected) {
                        setSelectedCategories((prev) =>
                          prev.map((v) => v.id).includes(value as string)
                            ? prev
                            : [...prev, selected!]
                        );
                      }
                    }}
                    onSearch={async (val) => {
                      if (val == "") return;
                      getProductCategories({
                        page: 1,
                        size: 10,
                        search: val,
                      })
                        .then((v) => v.json())
                        .then((v) => {
                          setCategories(v.data.items);
                        });
                    }}
                  />
                  <div className="flex flex-row flex-wrap mt-4 gap-y-2">
                    {selectedCategories.map((v) => (
                      <Tag
                        className=""
                        key={v.id}
                        closable
                        onClose={() => {
                          setSelectedProducts((prev) =>
                            prev.filter((vv) => vv.id !== v.id)
                          );
                        }}
                        color={"green"}
                      >
                        {v.name}
                      </Tag>
                    ))}
                  </div>
                  {selectedCategories.length > 0 && (
                    <small>{selectedCategories.length} selected</small>
                  )}
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div className="pt-4">
            <Button appearance="primary" onClick={saveNewRule}>
              Submit
            </Button>
            <Button appearance="link" onClick={() => setModalAddRule(false)}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        size={"lg"}
        open={modalAddAction}
        onClose={() => setModalAddAction(false)}
      >
        <Modal.Header>
          <Modal.Title>Add Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="w-full" layout="horizontal" fluid>
            <div className="form-group">
              <label>Type</label>
              <div className="input">
                <SelectPicker
                  block
                  data={[
                    { label: "Discount", value: "DISCOUNT" },
                    { label: "Discount Percent", value: "DISCOUNT_PERCENT" },
                    { label: "Free Shipping", value: "FREE_SHIPPING" },
                    { label: "Discount Shipping", value: "DISCOUNT_SHIPPING" },
                    { label: "Discount Amount Shipping", value: "DISCOUNT_AMOUNT_SHIPPING" },
                    { label: "Free Item", value: "FREE_ITEM" },
                  ]}
                  searchable={false}
                  onChange={(value) => {
                    setModalAddActionType(value!);
                  }}
                  value={modalAddActionType}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Value</label>
              <div className="input relative">
                <Input
                  name="value"
                  value={modalAddActionValue}
                  onChange={(e) => {
                      setModalAddActionValue(e);
                  }}
                  />
                  {(modalAddActionType == "DISCOUNT_PERCENT" || modalAddActionType == "DISCOUNT_SHIPPING" )&& <span className="absolute top-0 right-0 bottom-0 flex items-center px-3 text-gray-500">%</span>}
                  {(modalAddActionType == "FREE_ITEM" )&& <span className="absolute top-0 right-0 bottom-0 flex items-center px-3 text-gray-500">pcs</span>}
              </div>
            </div>
          
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div className="pt-4">
            <Button appearance="primary" onClick={saveNewAction}>
              Submit
            </Button>
            <Button appearance="link" onClick={() => setModalAddAction(false)}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </MainLayout>
  );
};
export default PromotionDetail;
